import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiService, CareerSiteData, hasInternalToken, setInternalToken, clearInternalToken, getResolvedClientUuid, setResolvedClientUuid as persistClientUuid, clearResolvedClientUuid, setReferralRequireEmployeeId, setReferralRequireReferrerIdentity, setReferralIsCvRequired, setReferralIsEmailRequired } from '../services/api';
import { useFavicon } from '../hooks/useFavicon';
import { getTranslations } from '../services/translations';
import { isCustomDomain, adaptApplyUrl, validateHostname } from '../utils/routing';
import { needsTapHandoff } from '../utils/browserContext';
import { useProbeData } from '../contexts/RegionContext';
import WhatsAppHandoff from '../components/WhatsAppHandoff';

interface CareerSiteProps {
  isInternal?: boolean;
  isReferral?: boolean;
}

// Group the options by state so long lists stay scannable. The value stays the
// city — that is what the job list and the `location` URL param filter on. One
// state stays flat: a lone header repeating every option below it is just noise.
const buildLocationOptions = (locations: string[], states: Record<string, string> = {}) => {
  const byState = new Map<string, string[]>();
  const ungrouped: string[] = [];
  locations.forEach((location) => {
    const state = states[location];
    if (!state) {
      ungrouped.push(location);
      return;
    }
    const group = byState.get(state);
    if (group) group.push(location);
    else byState.set(state, [location]);
  });

  const renderOption = (location: string) => (
    <option key={location} value={location}>{location}</option>
  );

  if (byState.size < 2) return locations.map(renderOption);

  return (
    <>
      {[...byState.keys()].sort((a, b) => a.localeCompare(b)).map((state) => (
        <optgroup key={state} label={state}>
          {byState.get(state)!.map(renderOption)}
        </optgroup>
      ))}
      {ungrouped.map(renderOption)}
    </>
  );
};

const CareerSite: React.FC<CareerSiteProps> = ({ isInternal = false, isReferral = false }) => {
  // Referral portal uses the same verification gate as internal
  const isGated = isInternal || isReferral;
  const { clientCode, clientUuid, apCode } = useParams<{ clientCode: string; clientUuid: string; apCode: string }>();
  const isOnCustomDomain = isCustomDomain();
  const clientSlug = clientCode;
  // clientCode from path mode routes, clientUuid from custom domain routes (backward compat)
  const code = clientCode || clientUuid;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [careerData, setCareerData] = useState<CareerSiteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<string>(searchParams.get('location') || '');
  const [selectedContractType, setSelectedContractType] = useState<string>(searchParams.get('contract_type') || '');
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>(() => {
    const attrs: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith('attr_')) {
        attrs[key.slice(5)] = value;
      }
    });
    return attrs;
  });
  const [translations, setTranslations] = useState<any>(() => getTranslations(navigator.language || 'es-es'));

  const openApplicationsRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const applyPageStyleRef = useRef<HTMLStyleElement | null>(null);
  const brandStyleRef = useRef<HTMLStyleElement | null>(null);
  const customJsScriptRef = useRef<HTMLScriptElement | null>(null);
  const [isSearchStuck, setIsSearchStuck] = useState(false);
  const isEmbed = searchParams.get('embed') === 'true';

  const probeCareerData = useProbeData<CareerSiteData>();

  // WhatsApp tracking link redirect: if ref param is present, resolve and redirect
  const ref = searchParams.get('ref');
  const strategyOverride = searchParams.get('strategy');
  const [redirecting, setRedirecting] = useState(!!ref);
  const [handoff, setHandoff] = useState<{
    url: string;
    phone: string;
    clientUuid?: string;
    position?: { uuid: string; name: string } | null;
  } | null>(null);

  useEffect(() => {
    if (!ref) return;
    apiService.resolveWhatsAppRedirect(ref)
      .then(({ url, phone, strategy, client_uuid, position_uuid, position_name }) => {
        if (!url || !url.startsWith('https://wa.me/')) {
          setRedirecting(false);
          return;
        }
        // A plain desktop browser completes the wa.me handoff on its own, so the
        // tap-through page is for phones and embedded browsers. `?strategy=` still
        // forces either path, for testing from a device the rule would exclude.
        const wantsTapPage =
          (strategyOverride || strategy) === 'interstitial' &&
          (strategyOverride === 'interstitial' || needsTapHandoff());

        if (wantsTapPage) {
          setHandoff({
            url,
            phone,
            clientUuid: client_uuid,
            position: position_uuid && position_name ? { uuid: position_uuid, name: position_name } : null,
          });
          // The handoff page stays on the career site, so its branding and
          // translations still have to load.
          setRedirecting(false);
          return;
        }
        window.location.href = url;
      })
      .catch(() => setRedirecting(false));
  }, [ref, strategyOverride]);

  useEffect(() => {
    return () => {
      applyPageStyleRef.current?.remove();
      brandStyleRef.current?.remove();
      customJsScriptRef.current?.remove();
    };
  }, []);

  useEffect(() => {
    if (isEmbed) return;
    const el = searchBarRef.current;
    if (!el) return;
    const onScroll = () => setIsSearchStuck(el.getBoundingClientRect().bottom <= 0);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isEmbed, careerData]);

  // Register tracking click (fire-and-forget)
  const trackingCodeParam = searchParams.get('tracking_code');
  useEffect(() => {
    if (trackingCodeParam) {
      apiService.registerTrackingClick(trackingCodeParam).catch(() => {});
    }
  }, [trackingCodeParam]);

  // Internal verification gate state
  const [requiresVerification, setRequiresVerification] = useState(true);
  const [isVerified, setIsVerified] = useState(() => !isGated || hasInternalToken());
  // Resolved client UUID for referral portal (persisted in localStorage to survive navigation)
  const [resolvedClientUuid, setResolvedClientUuid] = useState<string | null>(getResolvedClientUuid);
  const [gateStep, setGateStep] = useState<'email' | 'otp'>('email');
  const [gateEmail, setGateEmail] = useState('');
  const [gateOtp, setGateOtp] = useState('');
  const [gateError, setGateError] = useState<string | null>(null);
  const [gateLoading, setGateLoading] = useState(false);

  const scrollToOpenApplications = () => {
    openApplicationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openApplicationTitle =
    careerData?.company.open_application_title ||
    translations?.['career_site.open_applications'] ||
    'Candidaturas Abiertas';

  const openApplicationDescription =
    careerData?.company.open_application_description ||
    translations?.['career_site.open_applications_subtitle'] ||
    '¿No encuentras lo que buscas? Envíanos tu CV y te contactaremos cuando surja una oportunidad.';

  const internalBannerTitle =
    careerData?.company.internal_banner_title ||
    translations?.['career_site.internal_banner_title'] ||
    'Internal job portal';

  const internalBannerDescription = careerData?.company.internal_banner_description || '';

  // The per-attribute key (`career_site.all_department`, …) is opt-in per client:
  // attribute codes are client-defined, so the generic key carries every other case.
  const attributeFilterAllLabel = useCallback((filter: { type_code: string; type_name: string }) => {
    const specific = translations?.[`career_site.all_${filter.type_code}`];
    if (specific) return specific;
    const generic = translations?.['career_site.all_attribute'] || '{type}: All';
    return generic.replace('{type}', filter.type_name);
  }, [translations]);

  // Format contract type using translations
  const formatContractType = useCallback((value: string): string => {
    if (!value) return '';
    // Normalize: "full-time" -> "full_time"
    const normalized = value.toLowerCase().trim().replace(/-/g, '_');
    const translationKey = `career_site.contract_type.${normalized}`;
    return translations?.[translationKey] || value;
  }, [translations]);

  // Cambiar favicon dinámicamente
  useFavicon(careerData?.company.favicon);

  const fetchCareerData = useCallback(async () => {
    if (!code && !apCode && !isOnCustomDomain) return;

    // The referral endpoint takes the client UUID, not the URL code. Until
    // the config fetch resolves it, skip — a fetch with the raw code 422s
    // and leaves a stale error over the later successful load.
    const referralClientUuid = resolvedClientUuid || getResolvedClientUuid();
    if (isReferral && !referralClientUuid) return;

    try {
      setLoading(true);
      setError(null);
      let data: CareerSiteData;
      if (probeCareerData && !isGated) {
        // Use pre-fetched probe data from region resolution
        data = probeCareerData;
      } else if (apCode) {
        data = await apiService.getApplySiteData(apCode, trackingCodeParam || undefined);
      } else if (isReferral) {
        data = await apiService.getReferralCareerSiteData(referralClientUuid!);
      } else if (isInternal) {
        data = await apiService.getInternalCareerSiteData(code!);
      } else if (isOnCustomDomain && !code) {
        // Custom domain root — look up by domain
        data = await apiService.getCareerSiteByDomain(window.location.hostname, trackingCodeParam || undefined);
      } else {
        data = await apiService.getCareerSiteData(code!, trackingCodeParam || undefined);
      }
      // Hostname validation: ensure the domain is allowed to serve this client
      const firstApplyUrl = data.jobs?.[0]?.apply_url || data.open_application_jobs?.[0]?.apply_url;
      if (!apCode && !validateHostname(data.company.custom_apply_domain, firstApplyUrl)) {
        navigate('/404', { replace: true });
        return;
      }

      setCareerData(data);

      // Get translations based on company language (default to Spanish)
      const companyTranslations = getTranslations(data.company.language_default || 'es-es');
      setTranslations(companyTranslations);

      // Apply custom CSS/JS from client data — replace any previously injected elements
      applyPageStyleRef.current?.remove();
      brandStyleRef.current?.remove();
      customJsScriptRef.current?.remove();
      applyPageStyleRef.current = null;
      brandStyleRef.current = null;
      customJsScriptRef.current = null;

      if (data.company.apply_page_css) {
        const style = document.createElement('style');
        style.textContent = data.company.apply_page_css;
        document.head.appendChild(style);
        applyPageStyleRef.current = style;

        // Extract brand color from h1.custom-color and apply to career site accent elements
        setTimeout(() => {
          const el = document.createElement('h1');
          el.className = 'custom-color';
          el.style.display = 'none';
          document.body.appendChild(el);
          const color = getComputedStyle(el).color;
          document.body.removeChild(el);

          if (color && color !== 'rgb(0, 0, 0)') {
            const brandStyle = document.createElement('style');
            brandStyle.textContent = `
              .attribute-badge { background-color: color-mix(in hsl, ${color} 15%, white); color: color-mix(in hsl, ${color} 70%, black); }
              button.custom-color:not(.border-dashed) { background-color: ${color} !important; color: white !important; }
              button.custom-color:not(.border-dashed):hover { filter: brightness(0.9); }
              .border-dashed.custom-color { border-color: ${color} !important; background-color: white !important; color: inherit !important; }
              .border-dashed.custom-color:hover { background-color: color-mix(in srgb, ${color} 8%, white) !important; }
              .custom-color svg { color: ${color} !important; }
              .group:hover .job-title-hover { color: ${color}; transition: color 0.15s; }
              .group:hover .job-arrow { color: ${color}; }
              .open-applications-bg { background: radial-gradient(ellipse at 90% 110%, color-mix(in srgb, ${color} 65%, black) 0%, #111111 60%) !important; }
              .open-applications-bg .brand-label { color: rgba(255,255,255,0.75) !important; }
            `;
            document.head.appendChild(brandStyle);
            brandStyleRef.current = brandStyle;
          }
        }, 0);
      }

      // Apply custom JS if available
      if (data.company.apply_page_js) {
        const script = document.createElement('script');
        script.textContent = data.company.apply_page_js;
        document.body.appendChild(script);
        customJsScriptRef.current = script;
      }
    } catch (err: any) {
      // Handle 401/403 for internal site — clear token and show gate again
      if (isGated && (err.response?.status === 401 || err.response?.status === 403)) {
        clearInternalToken();
        clearResolvedClientUuid();
        setIsVerified(false);
        setGateStep('email');
        setLoading(false);
        return;
      }

      setError(`Error loading career site data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [code, apCode, isInternal, isReferral, isGated, isOnCustomDomain, resolvedClientUuid, trackingCodeParam, probeCareerData]);

  useEffect(() => {
    if (redirecting) return;

    if (apCode || !isGated) {
      fetchCareerData();
      return;
    }

    // For gated sites (internal/referral), check if verification is required
    const init = async () => {
      if (!code) return;

      let verified = hasInternalToken();

      try {
        if (isReferral) {
          const config = await apiService.getReferralConfig(code);
          setRequiresVerification(config.requires_verification);
          setResolvedClientUuid(config.client_uuid);
          persistClientUuid(config.client_uuid);
          setReferralRequireEmployeeId(config.require_employee_id ?? false);
          setReferralRequireReferrerIdentity(config.require_referrer_identity ?? false);
          setReferralIsCvRequired(config.is_cv_required ?? true);
          setReferralIsEmailRequired(config.is_email_required ?? true);
          if (!config.requires_verification) {
            verified = true;
            setIsVerified(true);
          }
        } else {
          const config = await apiService.getInternalConfig(code);
          setRequiresVerification(config.requires_verification);
          if (!config.requires_verification) {
            verified = true;
            setIsVerified(true);
          }
        }
      } catch {
        // On error, keep safe default (requires verification)
      }

      if (verified) {
        setIsVerified(true);
        // If there's a returnTo param, navigate there instead of loading career site
        const returnTo = searchParams.get('returnTo');
        if (returnTo) {
          navigate(returnTo);
          return;
        }
        fetchCareerData();
      } else {
        setLoading(false);
      }
    };

    init();
  }, [isGated, isReferral, code, redirecting]);

  // Fetch career data when verification completes (after OTP)
  useEffect(() => {
    if (isGated && isVerified && !careerData) {
      // If there's a returnTo param, navigate there after verification
      const returnTo = searchParams.get('returnTo');
      if (returnTo) {
        navigate(returnTo);
        return;
      }
      fetchCareerData();
    }
  }, [isGated, isVerified]);

  // For OTP calls, use resolved UUID for referral portal (backend expects UUID)
  const otpClientId = (isReferral && resolvedClientUuid) ? resolvedClientUuid : code;

  // Handle email submit for verification gate
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpClientId || !gateEmail) return;

    setGateLoading(true);
    setGateError(null);

    try {
      await apiService.requestInternalCode(gateEmail, otpClientId);
      setGateStep('otp');
    } catch (err: any) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403) {
        setGateStep('otp');
        return;
      } else {
        setGateError(detail || 'An error occurred. Please try again.');
      }
    } finally {
      setGateLoading(false);
    }
  };

  // Handle OTP submit for verification gate
  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpClientId || !gateOtp) return;

    setGateLoading(true);
    setGateError(null);

    try {
      const result = await apiService.verifyInternalCode(gateEmail, gateOtp, otpClientId);
      setInternalToken(result.token);
      setLoading(true);
      setIsVerified(true);
    } catch (err: any) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) {
        setGateError(detail || 'Too many attempts. Please request a new code.');
        setGateStep('email');
        setGateOtp('');
      } else {
        setGateError(detail || 'Invalid verification code');
      }
    } finally {
      setGateLoading(false);
    }
  };

  // Handle resend code
  const handleResendCode = async () => {
    if (!otpClientId || !gateEmail) return;

    setGateLoading(true);
    setGateError(null);

    try {
      await apiService.requestInternalCode(gateEmail, otpClientId);
      setGateOtp('');
    } catch (err: any) {
      setGateError(err.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setGateLoading(false);
    }
  };

  // Update URL params when filters change
  const updateFilters = (location: string, contractType: string, attributes: Record<string, string> = selectedAttributes) => {
    const newParams = new URLSearchParams();
    // Preserve tracking params across filter changes
    const tc = searchParams.get('tracking_code');
    if (tc) newParams.set('tracking_code', tc);
    if (location) newParams.set('location', location);
    if (contractType) newParams.set('contract_type', contractType);
    Object.entries(attributes).forEach(([key, value]) => {
      if (value) newParams.set(`attr_${key}`, value);
    });
    setSearchParams(newParams);
  };

  // Handle attribute filter change
  const handleAttributeFilterChange = (typeCode: string, value: string) => {
    const newAttributes = { ...selectedAttributes, [typeCode]: value };
    if (!value) delete newAttributes[typeCode];
    setSelectedAttributes(newAttributes);
    updateFilters(selectedLocation, selectedContractType, newAttributes);
  };

  const filteredJobs = careerData?.jobs.filter(job => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || job.name.toLowerCase().includes(q) || job.location.toLowerCase().includes(q) || job.contract_type.toLowerCase().includes(q);

    const allowedLocs = selectedLocation ? (careerData?.location_groups?.[selectedLocation] || [selectedLocation]) : null;
    const matchesLocation = !allowedLocs || allowedLocs.some(l => l.toLowerCase() === job.location.toLowerCase());

    const matchesContract = !selectedContractType || job.contract_type.toLowerCase() === selectedContractType.toLowerCase();

    const matchesAttrs = Object.entries(selectedAttributes).every(([code, val]) => {
      if (!val) return true;
      const filter = careerData?.attribute_filters?.find(f => f.type_code === code);
      return job.attributes?.some(a => a.type_name === filter?.type_name && a.value === val);
    });

    return matchesSearch && matchesLocation && matchesContract && matchesAttrs;
  }) || [];

  // Render verification gate for internal site
  if (isGated && requiresVerification && !isVerified) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {translations?.['career_site.internal_verify_title'] || 'Verify your identity'}
            </h2>
          </div>

          {gateStep === 'email' ? (
            <form onSubmit={handleEmailSubmit}>
              <label htmlFor="gate-email" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.['career_site.internal_verify_email_label'] || 'Work email'}
              </label>
              <input
                id="gate-email"
                type="email"
                required
                value={gateEmail}
                onChange={(e) => setGateEmail(e.target.value)}
                placeholder={translations?.['career_site.internal_verify_email_placeholder'] || 'your.name@company.com'}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-base mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {gateError && (
                <div className="text-red-500 text-sm mb-4">{gateError}</div>
              )}
              <button
                type="submit"
                disabled={gateLoading}
                className="w-full bg-gray-900 text-white font-semibold py-2.5 rounded-lg hover:bg-gray-800 transition disabled:opacity-60"
              >
                {gateLoading ? '...' : (translations?.['career_site.internal_verify_email_submit'] || 'Send verification code')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit}>
              <p className="text-sm text-gray-500 mb-4">
                {translations?.['career_site.internal_verify_otp_sent'] || 'If your email is valid, you will receive a verification code.'} ({gateEmail})
              </p>
              <label htmlFor="gate-otp" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.['career_site.internal_verify_otp_label'] || 'Verification code'}
              </label>
              <input
                id="gate-otp"
                type="text"
                required
                maxLength={6}
                pattern="[0-9]{6}"
                inputMode="numeric"
                value={gateOtp}
                onChange={(e) => setGateOtp(e.target.value.replace(/\D/g, ''))}
                placeholder={translations?.['career_site.internal_verify_otp_placeholder'] || 'Enter 6-digit code'}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-base text-center tracking-widest mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {gateError && (
                <div className="text-red-500 text-sm mb-4">{gateError}</div>
              )}
              <button
                type="submit"
                disabled={gateLoading || gateOtp.length !== 6}
                className="w-full bg-gray-900 text-white font-semibold py-2.5 rounded-lg hover:bg-gray-800 transition disabled:opacity-60 mb-3"
              >
                {gateLoading ? '...' : (translations?.['career_site.internal_verify_otp_submit'] || 'Verify')}
              </button>
              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setGateStep('email'); setGateOtp(''); setGateError(null); }}
                  className="text-gray-500 hover:text-gray-700 transition"
                >
                  {translations?.['career_site.internal_verify_otp_back'] || 'Use a different email'}
                </button>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={gateLoading}
                  className="text-gray-500 hover:text-gray-700 transition disabled:opacity-60"
                >
                  {translations?.['career_site.internal_verify_otp_resend'] || 'Resend code'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{translations?.['career_site.loading'] || 'Loading career site...'}</p>
        </div>
      </div>
    );
  }

  // After `loading`, so the client's branding and language are in hand, but
  // before the not-found guard: a career site that fails to load must not take
  // the WhatsApp handoff down with it.
  if (handoff) {
    return (
      <WhatsAppHandoff
        url={handoff.url}
        phone={handoff.phone}
        translations={translations}
        company={careerData?.company}
        clientUuid={handoff.clientUuid}
        position={handoff.position}
        trackingCode={ref}
      />
    );
  }

  if (error || !careerData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{translations?.['career_site.not_found'] || 'Career Site Not Found'}</h1>
          <p className="text-gray-600">{translations?.['career_site.not_found_message'] || "The career site you're looking for could not be found."}</p>
        </div>
      </div>
    );
  }

  // Build job link URL based on internal/public mode, preserving tracking_code
  const trackingCode = searchParams.get('tracking_code');

  const getJobLink = (job: { apply_url: string }) => {
    let url: string;
    if (isInternal || isReferral) {
      // apply_url is a full URL; extract pathname to parse segments
      // pathname format: /{clientSlug}/{posSlug}/{posUuid}
      // Convert to portal route: /internal/{clientCode}/{posSlug}/{posUuid}
      //                      or: /referral/{clientCode}/{posSlug}/{posUuid}
      const portalPrefix = isReferral ? 'referral' : 'internal';
      let pathname: string;
      try { pathname = new URL(job.apply_url).pathname; } catch { pathname = job.apply_url; }
      const parts = pathname.split('/').filter(Boolean);
      // parts: [clientSlug, posSlug, posUuid] or [posSlug, posUuid] (custom domain)
      if (parts.length >= 2) {
        const posSlug = parts[parts.length - 2];
        const posUuid = parts[parts.length - 1];
        url = `/${portalPrefix}/${code}/${posSlug}/${posUuid}`;
      } else {
        url = pathname;
      }
    } else if (apCode) {
      // Apply site mode: links must stay under /sites/{apCode}/…
      let pathname: string;
      try { pathname = new URL(job.apply_url).pathname; } catch { pathname = job.apply_url; }
      const parts = pathname.split('/').filter(Boolean);
      // parts: [clientSlug, posSlug, posUuid] — take last two segments
      if (parts.length >= 2) {
        const posSlug = parts[parts.length - 2];
        const posUuid = parts[parts.length - 1];
        url = `/sites/${apCode}/${posSlug}/${posUuid}`;
      } else {
        url = pathname;
      }
    } else {
      url = adaptApplyUrl(job.apply_url);
    }
    if (trackingCode) {
      const separator = url.includes('?') ? '&' : '?';
      url += `${separator}tracking_code=${encodeURIComponent(trackingCode)}`;
    }
    return url;
  };

  const searchContent = careerData && (() => {
    const hasLocation = careerData.locations && careerData.locations.length > 0;
    const hasContract = careerData.company?.show_contract_type_filter !== false && careerData.contract_types && careerData.contract_types.length > 0;
    const hasAttributes = careerData.attribute_filters && careerData.attribute_filters.length > 0;
    const filterCount = (hasLocation ? 1 : 0) + (hasContract ? 1 : 0) + (hasAttributes ? careerData.attribute_filters!.length : 0);
    const singleFilter = filterCount === 1;
    const selectClass = "orbio-select pl-4 pr-9 py-2.5 border border-[#dde0d4] rounded-lg text-[14px] bg-white text-[#1e2418] cursor-pointer hover:border-[#b8bda8] focus:outline-none focus:border-[#7d8c70] focus-visible:ring-2 focus-visible:ring-[#3d4a2e] focus-visible:ring-offset-2";
    const locationOptions = buildLocationOptions(careerData.locations, careerData.location_states);

    return singleFilter ? (
      <div className="flex gap-3">
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666d5a]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input
            type="text"
            aria-label={translations?.['career_site.search_placeholder'] || 'Buscar ofertas'}
            className="w-full pl-10 pr-4 py-2.5 border border-[#dde0d4] rounded-lg text-[14px] text-[#1e2418] placeholder-[#a3a996] bg-white focus:outline-none focus:border-[#7d8c70] focus-visible:ring-2 focus-visible:ring-[#3d4a2e] focus-visible:ring-offset-2"
            placeholder={translations?.['career_site.search_placeholder'] || 'Buscar ofertas por nombre, localización…'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        {hasLocation && (
          <select
            aria-label={translations?.['career_site.all_locations'] || 'Ubicaciones'}
            value={selectedLocation}
            onChange={(e) => { setSelectedLocation(e.target.value); updateFilters(e.target.value, selectedContractType); }}
            className={`${selectClass} w-[30%] max-w-[220px]`}
          >
            <option value="">{translations?.['career_site.all_locations'] || 'Todas las ubicaciones'}</option>
            {locationOptions}
          </select>
        )}
        {hasContract && (
          <select
            aria-label={translations?.['career_site.all_contract_types'] || 'Tipos de contrato'}
            value={selectedContractType}
            onChange={(e) => { setSelectedContractType(e.target.value); updateFilters(selectedLocation, e.target.value); }}
            className={`${selectClass} w-[30%] max-w-[220px]`}
          >
            <option value="">{translations?.['career_site.all_contract_types'] || 'Todos los contratos'}</option>
            {careerData.contract_types.map((contractType) => (
              <option key={contractType} value={contractType}>{formatContractType(contractType)}</option>
            ))}
          </select>
        )}
        {careerData.attribute_filters?.map((filter) => (
          <select
            key={filter.type_code}
            aria-label={attributeFilterAllLabel(filter)}
            value={selectedAttributes[filter.type_code] || ''}
            onChange={(e) => handleAttributeFilterChange(filter.type_code, e.target.value)}
            className={`${selectClass} w-[30%] max-w-[220px]`}
          >
            <option value="">{attributeFilterAllLabel(filter)}</option>
            {filter.values.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        ))}
      </div>
    ) : (
      <div className="space-y-3">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666d5a]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input
            type="text"
            aria-label={translations?.['career_site.search_placeholder'] || 'Buscar ofertas'}
            className="w-full pl-10 pr-4 py-2.5 border border-[#dde0d4] rounded-lg text-[14px] text-[#1e2418] placeholder-[#a3a996] bg-white focus:outline-none focus:border-[#7d8c70] focus-visible:ring-2 focus-visible:ring-[#3d4a2e] focus-visible:ring-offset-2"
            placeholder={translations?.['career_site.search_placeholder'] || 'Buscar ofertas por nombre, localización…'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          {hasLocation && (
            <select
              aria-label={translations?.['career_site.all_locations'] || 'Ubicaciones'}
              value={selectedLocation}
              onChange={(e) => { setSelectedLocation(e.target.value); updateFilters(e.target.value, selectedContractType); }}
              className={`${selectClass} flex-1 min-w-[180px]`}
            >
              <option value="">{translations?.['career_site.all_locations'] || 'Todas las ubicaciones'}</option>
              {locationOptions}
            </select>
          )}
          {hasContract && (
            <select
              aria-label={translations?.['career_site.all_contract_types'] || 'Tipos de contrato'}
              value={selectedContractType}
              onChange={(e) => { setSelectedContractType(e.target.value); updateFilters(selectedLocation, e.target.value); }}
              className={`${selectClass} flex-1 min-w-[180px]`}
            >
              <option value="">{translations?.['career_site.all_contract_types'] || 'Todos los contratos'}</option>
              {careerData.contract_types.map((contractType) => (
                <option key={contractType} value={contractType}>{formatContractType(contractType)}</option>
              ))}
            </select>
          )}
          {careerData.attribute_filters?.map((filter) => (
            <select
              key={filter.type_code}
              aria-label={attributeFilterAllLabel(filter)}
              value={selectedAttributes[filter.type_code] || ''}
              onChange={(e) => handleAttributeFilterChange(filter.type_code, e.target.value)}
              className={`${selectClass} flex-1 min-w-[180px]`}
            >
              <option value="">{attributeFilterAllLabel(filter)}</option>
              {filter.values.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          ))}
        </div>
      </div>
    );
  })();

  return (
    <div className={isEmbed ? "bg-white" : "bg-[#fafaf7] min-h-screen flex flex-col"}>
      <main className="flex-1">
      {!isEmbed && (
        <>
          {/* Logo centered over banner */}
          <div className="relative w-full flex justify-center">
            {careerData.company.logo ? (
              <img
                src={careerData.company.logo}
                alt={`Logo ${careerData.company.name}`}
                className="h-20 w-auto rounded-2xl shadow bg-white z-20 -mb-8 mt-8 border-4 border-white"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const placeholder = target.nextElementSibling as HTMLElement;
                  if (placeholder) placeholder.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className="h-20 w-20 rounded-2xl shadow bg-white z-20 -mb-8 mt-8 border-4 border-white flex items-center justify-center text-[#3d4a2e] font-bold text-2xl select-none"
              style={{ display: careerData.company.logo ? 'none' : 'flex' }}
            >
              {careerData.company.name?.slice(0, 2).toUpperCase() || 'OR'}
            </div>
          </div>

          {/* Company banner */}
          <div
            className="banner w-full h-72 bg-cover bg-center"
            style={{ backgroundImage: `url('${careerData.company.main_image || `https://picsum.photos/seed/${encodeURIComponent(careerData.company.name || 'orbio')}/1400/400`}')` }}
          ></div>

          {/* Search bar — bridges hero/content boundary */}
          <div
            ref={searchBarRef}
            className="relative z-20 w-full -mt-8"
          >
            <div className="max-w-5xl mx-auto px-4 md:px-8">
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.10)] border border-[#dde0d4] px-5 py-4">
                {searchContent}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Ghost search bar — fixed clone that fades in when real bar scrolls off viewport */}
      {!isEmbed && (
        <div
          className={`fixed top-0 left-0 right-0 z-50 bg-[#fafaf7] pt-3 pb-3 transition-opacity duration-150 shadow-[0_2px_12px_rgba(0,0,0,0.06)] ${isSearchStuck ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          hidden={!isSearchStuck}
        >
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.10)] border border-[#dde0d4] px-5 py-4">
              {searchContent}
            </div>
          </div>
        </div>
      )}

      {/* Jobs section */}
      <div className={isEmbed ? "max-w-5xl mx-auto p-4" : "max-w-5xl mx-auto w-full px-4 md:px-8 pt-6 pb-8 z-10 relative"}>

        {/* Internal/Referral site banner */}
        {!isEmbed && isGated && (
          <div className={`mb-6 px-5 py-4 bg-white border border-[#dde0d4] rounded-xl flex ${!isReferral && internalBannerDescription ? 'items-start' : 'items-center'}`}>
            <div className={`flex ${!isReferral && internalBannerDescription ? 'items-start' : 'items-center'} gap-3`}>
              <div className="w-8 h-8 bg-[#e4e7dc] rounded-full flex items-center justify-center shrink-0">
                <svg className="w-4 h-4 text-[#555c4a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isReferral ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  )}
                </svg>
              </div>
              {!isReferral && internalBannerDescription ? (
                <div>
                  <div className="text-sm font-medium text-[#1e2418] mb-1">{internalBannerTitle}</div>
                  <div className="text-sm text-[#666d5a]">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: ({ children }) => <h1 className="text-base font-bold text-[#1e2418] mb-2">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-sm font-bold text-[#1e2418] mb-2">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-sm font-semibold text-[#1e2418] mb-1">{children}</h3>,
                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                        ul: ({ children }) => <ul className="list-disc list-inside mb-2">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
                        li: ({ children }) => <li className="mb-1">{children}</li>,
                        strong: ({ children }) => <strong className="font-semibold text-[#1e2418]">{children}</strong>,
                        em: ({ children }) => <em className="italic">{children}</em>,
                        a: ({ href, children }) => (
                          <a href={href} className="text-[#1e2418] underline hover:opacity-80" target="_blank" rel="noopener noreferrer">
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {internalBannerDescription}
                    </ReactMarkdown>
                  </div>
                </div>
              ) : (
                <div>
                  <span className="text-sm font-medium text-[#1e2418]">
                    {isReferral
                      ? (translations?.['career_site.referral_banner_title'] || 'Referral portal')
                      : internalBannerTitle}
                  </span>
                  <span className="text-sm text-[#666d5a] ml-2">
                    — {isReferral
                      ? (translations?.['career_site.referral_banner_description'] || 'Refer candidates for open positions')
                      : (translations?.['career_site.internal_banner_description'] || 'This page is for employees only. If you are an external candidate, visit our')}{' '}
                    {!isReferral && (
                      <a
                        href={isOnCustomDomain ? '/' : `/${clientSlug}`}
                        className="text-[#1e2418] underline hover:opacity-80"
                      >
                        {translations?.['career_site.internal_banner_link'] || 'public job page'}
                      </a>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Search/filter — embed mode only (non-embed renders the bridge above the hero) */}
        {isEmbed && (
          <div className="mb-4">
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.10)] border border-[#dde0d4] px-5 py-4">
              {searchContent}
            </div>
          </div>
        )}

        {/* "Open applications" inline CTA */}
        {!isEmbed && careerData?.open_application_jobs && careerData.open_application_jobs.length > 0 && (
          <button
            onClick={scrollToOpenApplications}
            className="w-full flex items-center justify-between px-5 py-[18px] bg-white border border-dashed border-[#dde0d4] rounded-xl hover:border-[#7d8c70] hover:bg-[#f0f2eb] transition text-left group custom-color mb-6"
          >
            <div>
              <div className="text-[15px] font-semibold text-[#1e2418]">{openApplicationTitle}</div>
              <div className="text-[13px] text-[#666d5a] mt-0.5">{openApplicationDescription}</div>
            </div>
            <svg className="w-4 h-4 text-[#666d5a] shrink-0 ml-4 transition" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        )}

        {/* Section title */}
        <div className="flex items-baseline justify-between mt-6 mb-4">
          <h1 className="text-[22px] font-bold tracking-tight uppercase text-[#1e2418]">
            {translations?.['career_site.open_roles'] || 'Ofertas Disponibles'}
          </h1>
          {filteredJobs.length > 0 && (
            <span className="text-[11px] font-semibold tracking-[0.1em] uppercase text-[#666d5a]">
              {filteredJobs.length} {filteredJobs.length === 1 ? (translations?.['career_site.roles_one'] || 'rol') : (translations?.['career_site.roles_other'] || 'roles')}
            </span>
          )}
        </div>

        {/* Job listings */}
        {filteredJobs.length > 0 ? (
          <div className="bg-white border border-[#dde0d4] rounded-xl overflow-hidden mb-4">
            {filteredJobs.map((job, index) => (
              <a
                key={index}
                href={getJobLink(job)}
                {...(isEmbed ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="flex items-center gap-4 md:gap-5 px-5 md:px-6 py-5 border-b border-[#dde0d4] last:border-b-0 hover:bg-[#f5f5f1] transition group"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[18px] font-serif font-normal text-[#1e2418] leading-snug tracking-[-0.005em] job-title-hover">
                    {job.name}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-[#666d5a] mt-1">
                    {job.contract_type && (
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
                        {formatContractType(job.contract_type)}
                      </span>
                    )}
                    {job.location && (
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {job.location}
                      </span>
                    )}
                    {job.join_date && (
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        {job.join_date}
                      </span>
                    )}
                  </div>
                  {job.attributes && job.attributes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {job.attributes.map((attr, i) => (
                        <span key={i} className="attribute-badge inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium">
                          {attr.value}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <svg className="job-arrow w-4 h-4 text-[#666d5a] group-hover:text-[#1e2418] transition shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center text-[#666d5a] py-12 text-[14px]">
            {translations?.['career_site.no_results'] || 'No se encontraron ofertas.'}
          </div>
        )}


      </div>

      {/* Open Applications Section — dark card */}
      {!isEmbed && careerData?.open_application_jobs && careerData.open_application_jobs.length > 0 && (
        <div ref={openApplicationsRef} className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-16 mt-6">
          <div className="open-applications-bg bg-black text-white rounded-2xl p-8 md:p-12">
            <div className="mb-6">
              <div className="brand-label text-[11px] font-semibold tracking-[0.14em] uppercase text-[#7CC19E] mb-3">
                {translations?.['career_site.open_applications'] || 'Candidaturas Abiertas'}
              </div>
              <h3 className="font-serif text-[28px] md:text-[34px] font-normal text-white leading-tight tracking-[-0.01em]">
                {openApplicationTitle}
              </h3>
              <p className="text-[14px] text-[rgba(255,255,255,0.6)] mt-2 max-w-xl leading-relaxed">
                {openApplicationDescription}
              </p>
            </div>
            <div className="border border-[rgba(255,255,255,0.1)] rounded-xl overflow-hidden">
              {careerData.open_application_jobs.map((job, index) => (
                <a
                  key={`open-${index}`}
                  href={getJobLink(job)}
                  className="flex items-center gap-4 px-5 py-4 border-b border-[rgba(255,255,255,0.08)] last:border-b-0 hover:bg-[rgba(255,255,255,0.05)] transition group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[17px] font-serif font-normal text-white leading-snug tracking-[-0.005em]">
                      {job.name}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-[rgba(255,255,255,0.5)] mt-1">
                      {job.contract_type && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
                          {formatContractType(job.contract_type)}
                        </span>
                      )}
                      {job.location && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          {job.location}
                        </span>
                      )}
                      {job.join_date && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                          {job.join_date}
                        </span>
                      )}
                    </div>
                    {job.attributes && job.attributes.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {job.attributes.map((attr, i) => (
                          <span key={i} className="attribute-badge inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium">
                            {attr.value}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <svg className="w-4 h-4 text-[rgba(255,255,255,0.35)] group-hover:text-white shrink-0 transition" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Company Description Section */}
      {!isEmbed && careerData.company.description && (
        <div className="w-full bg-[#edefe8] py-16 px-4 career-site-company-description">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#666d5a] mb-3">
                {translations?.['career_site.about'] || 'Sobre nosotros'}
              </div>
              <h2 className="font-serif text-[32px] md:text-[40px] font-normal text-[#1e2418] tracking-[-0.01em]">
                {careerData.company.name}
              </h2>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-12 border border-[#dde0d4]">
              <div className="prose prose-lg max-w-none text-[#3a3f2e] leading-relaxed">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => <h1 className="text-lg font-bold mb-3">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-sm font-bold mb-2">{children}</h3>,
                    h4: ({ children }) => <h4 className="text-sm font-semibold mb-2">{children}</h4>,
                    p: ({ children }) => <p className="mb-3 text-justify md:text-left text-base md:text-lg">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc list-inside mb-3">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal list-inside mb-3">{children}</ol>,
                    li: ({ children }) => <li className="mb-1">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                    a: ({ href, children }) => (
                      <a href={href} className="text-[#6BAF8D] hover:text-[#5A9E7C] underline" target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    ),
                  }}
                >
                  {careerData.company.description || ''}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}

      </main>
      {/* Footer */}
      {!isEmbed && <footer className="w-full text-center text-xs text-[#666d5a] py-8 mt-auto">
        <div className="flex flex-col items-center gap-2">
          <div>&copy; {new Date().getFullYear()} Orbio</div>
          {(clientSlug || apCode || isOnCustomDomain) && (
            <div className="flex gap-4">
              <a
                href={apCode ? `/sites/${apCode}/privacy-policy` : isOnCustomDomain ? '/privacy-policy' : `/${clientSlug}/privacy-policy`}
                className="text-[#666d5a] hover:text-[#1e2418] transition"
              >
                {translations?.['apply.privacy_policy_link'] || 'Privacy Policy'}
              </a>
              {careerData.company.terms_of_service && (
                <a
                  href={apCode ? `/sites/${apCode}/terms-of-service` : isOnCustomDomain ? '/terms-of-service' : `/${clientSlug}/terms-of-service`}
                  className="text-[#666d5a] hover:text-[#1e2418] transition"
                >
                  {translations?.['apply.terms_of_service_link'] || 'Terms of Service'}
                </a>
              )}
            </div>
          )}
          {careerData.company.social_links && (
            <div className="flex gap-4 justify-center mt-2">
              {careerData.company.social_links.linkedin && (
                <a href={careerData.company.social_links.linkedin} target="_blank" rel="noopener" aria-label="LinkedIn" className="hover:text-blue-600 transition text-lg">
                  <i className="fab fa-linkedin"></i>
                </a>
              )}
              {careerData.company.social_links.instagram && (
                <a href={careerData.company.social_links.instagram} target="_blank" rel="noopener" aria-label="Instagram" className="hover:text-pink-500 transition text-lg">
                  <i className="fab fa-instagram"></i>
                </a>
              )}
              {careerData.company.social_links.twitter && (
                <a href={careerData.company.social_links.twitter} target="_blank" rel="noopener" aria-label="Twitter" className="hover:text-blue-400 transition text-lg">
                  <i className="fab fa-twitter"></i>
                </a>
              )}
            </div>
          )}
        </div>
      </footer>}
    </div>
  );
};

export default CareerSite;
